import React, { useState } from 'react';
import { FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PropTypes from 'prop-types';
import './css/CollapsibleTable.css';

const ROWS_PER_PAGE = 5;

const CollapsibleTable = ({ 
  title, 
  data, 
  columns, 
  formatCurrency, 
  defaultCollapsed = true,
  customRowRenderer 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [currentPage, setCurrentPage] = useState(1);

  const toggleTableSection = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Pagination logic
  const totalPages = Math.ceil(data.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedData = data.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset to first page when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  return (
    <section className="collapsible-table-section">
      <div className="collapsible-table-header" onClick={toggleTableSection}>
        <h3>{title}</h3>
        <div className="collapse-toggle">
          <span>{isCollapsed ? 'Expand' : 'Collapse'}</span>
          <FaChevronDown className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`} />
        </div>
      </div>
      <div className={`table-container ${isCollapsed ? 'collapsed' : ''}`}>
        <table>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={index}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={index}>
                {customRowRenderer ? 
                  customRowRenderer(row, columns) : 
                  columns.map((column, colIndex) => (
                    <td key={colIndex} data-label={column.header}>
                      {column.render ? (
                        column.render(row[column.key], row)
                      ) : (
                        row[column.key]
                      )}
                    </td>
                  ))
                }
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            <div className="pagination-info">
              Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length} entries
            </div>
            <div className="pagination-buttons">
              <button
                className="pagination-btn"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
                Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

CollapsibleTable.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      render: PropTypes.func
    })
  ).isRequired,
  formatCurrency: PropTypes.func,
  defaultCollapsed: PropTypes.bool,
  customRowRenderer: PropTypes.func
};

CollapsibleTable.defaultProps = {
  defaultCollapsed: true,
  formatCurrency: (amount) => new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
  }).format(amount)
};

export default CollapsibleTable;
